import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';
import { ApiResponse, LayerDefinition } from '@packages/database/types';
import api from '@/api/client/axios';

interface FeatureWithLayer {
  feature: GeoJSON.Feature;
  layerDef: LayerDefinition;
}

const assetFeatureKeys = {
  asset: (orgId: string, assetType: string, assetId: string) =>
    ['layers', orgId, 'asset-feature', assetType, assetId] as const,
  premise: (orgId: string, premiseId: string) =>
    ['layers', orgId, 'premise-feature', premiseId] as const,
};

export const useAssetFeature = (
  assetType: string | null | undefined,
  assetId: string | null | undefined,
) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery<FeatureWithLayer>({
    queryKey: assetFeatureKeys.asset(orgId!, assetType!, assetId!),
    queryFn: () =>
      api
        .get<ApiResponse<FeatureWithLayer>>('/layers/asset-feature', {
          params: { assetType, assetId },
        })
        .then((r) => r.data.data!),
    enabled: !!orgId && !!assetType && !!assetId,
    staleTime: 5 * 60 * 1000, // 5 min — asset properties rarely change mid-session
  });
};

export const usePremiseFeature = (premiseId: string | null | undefined) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery<FeatureWithLayer>({
    queryKey: assetFeatureKeys.premise(orgId!, premiseId!),
    queryFn: () =>
      api
        .get<ApiResponse<FeatureWithLayer>>('/layers/premise-feature', {
          params: { premiseId },
        })
        .then((r) => r.data.data!),
    enabled: !!orgId && !!premiseId,
    staleTime: 5 * 60 * 1000,
  });
};
